import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContacts } from '../hooks/useContacts';
import { useSettings } from '../hooks/useSettings';
import { ContactList, CONTACT_TABLE_FIXED_COLUMNS } from '../components/ContactList';
import { BulkEditModal } from '../components/BulkEditModal';
import { ChatwootSyncModal } from '../components/ChatwootSyncModal';
import { PerfexImportModal } from '../components/PerfexImportModal';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';
import { Pagination } from '../components/Pagination';
import { Header } from '../components/Header';
import type { Contact } from '../types';
import type { ContactStats } from '../types';
import { apiService } from '../services/api';
import { Portal } from '../components/Portal';

export function ContactsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
  const [isChatwootSyncModalOpen, setIsChatwootSyncModalOpen] = useState(false);
  const [isPerfexImportModalOpen, setIsPerfexImportModalOpen] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [selectAllMode, setSelectAllMode] = useState<'page' | 'all' | null>(null);
  const [sortField, setSortField] = useState<'nome' | 'categoria' | 'atualizadoEm' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [categories, setCategories] = useState<Array<{ id: string; nome: string }>>([]);
  const [contactStats, setContactStats] = useState<ContactStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [allCustomFields, setAllCustomFields] = useState<Array<{ key: string; label: string }>>([]);

  const CONTACT_TABLE_STORAGE_KEY = 'contact_table_visible_columns';
  const DEFAULT_VISIBLE_COLUMNS = ['nome', 'telefone', 'email', 'categoria', 'atualizadoEm'];

  const [visibleColumnIds, setVisibleColumnIds] = useState<string[]>(() => {
    try {
      const s = localStorage.getItem(CONTACT_TABLE_STORAGE_KEY);
      if (s) {
        const parsed = JSON.parse(s) as string[];
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {}
    return DEFAULT_VISIBLE_COLUMNS;
  });

  const pageSize = 30;

  const { settings } = useSettings();

  const fetchStats = () => {
    setStatsLoading(true);
    apiService.getContactStats().then((s) => setContactStats(s)).catch(() => setContactStats(null)).finally(() => setStatsLoading(false));
  };

  useEffect(() => {
    apiService.getAllCategories().then((list) => setCategories(list || [])).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    apiService.getCustomFields().then((list) => setAllCustomFields(list.map((f) => ({ key: f.key, label: f.label })))).catch(() => setAllCustomFields([]));
  }, []);

  const handleVisibleColumnsChange = (ids: string[]) => {
    setVisibleColumnIds(ids);
    try {
      localStorage.setItem(CONTACT_TABLE_STORAGE_KEY, JSON.stringify(ids));
    } catch {}
  };

  const customFieldColumns = allCustomFields.filter((f) => visibleColumnIds.includes(f.key));

  const allAvailableColumns = [...CONTACT_TABLE_FIXED_COLUMNS, ...allCustomFields.map((f) => ({ id: f.key, label: f.label }))];
  const toggleColumn = (id: string) => {
    const next = visibleColumnIds.includes(id) ? visibleColumnIds.filter((c) => c !== id) : [...visibleColumnIds, id];
    if (next.length > 0) handleVisibleColumnsChange(next);
  };
  const columnPickerRef = useRef<HTMLDivElement>(null);
  const columnPickerButtonRef = useRef<HTMLButtonElement>(null);
  const [columnPickerPosition, setColumnPickerPosition] = useState<{ top: number; left: number } | null>(null);

  const categoryPickerRef = useRef<HTMLDivElement>(null);
  const categoryPickerButtonRef = useRef<HTMLButtonElement>(null);
  const [categoryPickerPosition, setCategoryPickerPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!columnPickerOpen) {
      setColumnPickerPosition(null);
      return;
    }
    const btn = columnPickerButtonRef.current;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setColumnPickerPosition({ top: rect.bottom + 4, left: rect.right - 224 });
    }
    const close = (e: MouseEvent) => {
      if (columnPickerRef.current && !columnPickerRef.current.contains(e.target as Node)) setColumnPickerOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [columnPickerOpen]);

  useEffect(() => {
    if (!categoryPickerOpen) {
      setCategoryPickerPosition(null);
      return;
    }
    const btn = categoryPickerButtonRef.current;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setCategoryPickerPosition({ top: rect.bottom + 4, left: rect.left });
    }
    const close = (e: MouseEvent) => {
      if (categoryPickerRef.current && !categoryPickerRef.current.contains(e.target as Node)) setCategoryPickerOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [categoryPickerOpen]);

  const { contacts, total, totalPages, loading, error, refresh, deleteContact } = useContacts({
    search: search || undefined,
    tag: selectedCategory || undefined,
    page: currentPage,
    pageSize,
    sortField: sortField || undefined,
    sortDirection: sortDirection,
  });

  // Verificar se Chatwoot está configurado
  const isChatwootConfigured = !!(
    settings?.chatwootUrl &&
    settings?.chatwootAccountId &&
    settings?.chatwootApiToken
  );

  // Verificar se Perfex está configurado
  const isPerfexConfigured = !!(
    settings?.perfexUrl &&
    settings?.perfexToken
  );

  const handleSearchChange = (newSearch: string) => {
    setSearch(newSearch);
    setCurrentPage(1);
  };

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearch('');
    setSelectedCategory('');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleEdit = (contact: Contact) => {
    navigate(`/contatos/${contact.id}`);
  };

  const handleToggleSelectContact = (contactId: string) => {
    setSelectedContactIds((prev) =>
      prev.includes(contactId)
        ? prev.filter((id) => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleSelectAllContacts = () => {
    if (selectedContactIds.length === contacts.length && selectAllMode === 'page') {
      // Se já tem todos da página selecionados, desmarcar
      setSelectedContactIds([]);
      setSelectAllMode(null);
    } else {
      // Selecionar todos da página atual
      setSelectedContactIds(contacts.map((c) => c.id));
      setSelectAllMode('page');
    }
  };

  const handleSelectAllGlobal = () => {
    // Marcar para selecionar TODOS os contatos (com filtros aplicados)
    setSelectAllMode('all');
  };

  const handleCancelGlobalSelection = () => {
    setSelectedContactIds([]);
    setSelectAllMode(null);
  };

  const handleOpenBulkEdit = () => {
    setIsBulkEditModalOpen(true);
  };

  const handleCloseBulkEdit = () => {
    setIsBulkEditModalOpen(false);
  };

  const handleBulkEditSuccess = () => {
    setSelectedContactIds([]);
    setSelectAllMode(null);
    setIsBulkEditModalOpen(false);
    refresh();
    fetchStats();
  };

  const handleSort = (field: 'nome' | 'categoria' | 'atualizadoEm') => {
    if (sortField === field) {
      // Se já está ordenando por esse campo, inverte a direção
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Se é um campo novo, começa com ascendente
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Voltar para primeira página ao ordenar
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Erro</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={refresh}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  const selectedCount = selectAllMode === 'all' ? total : selectedContactIds.length;
  const hasSelection = selectedCount > 0;

  return (
    <>
      <Header
        title="Contatos"
        subtitle={hasSelection ? `${selectedCount} contato(s) selecionado(s)` : `${total} contatos cadastrados`}
        compactTopbar
      />

      <PageLayout>
        <PageHeader
          title="Contatos"
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Contatos' },
          ]}
        />
        <div className="space-y-8 w-full">
          {/* Overview - mesmo padrão do Dashboard / WhatsApp */}
          <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">Visão dos contatos</h2>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <button
                  type="button"
                  onClick={() => { refresh(); fetchStats(); }}
                  className="btn-atualizar"
                  title="Atualizar lista"
                >
                  <i className="fas fa-arrows-rotate text-base shrink-0" aria-hidden />
                  Atualizar
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/contatos/categorias')}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors"
                  aria-label="Gerenciar categorias"
                >
                  <i className="fas fa-tags text-base shrink-0" aria-hidden />
                  Categorias
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/contatos/campos-personalizados')}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors"
                  aria-label="Campos personalizados"
                >
                  <i className="fas fa-sliders text-base shrink-0" aria-hidden />
                  Campos personalizados
                </button>
                {(isChatwootConfigured || isPerfexConfigured) && (
                  <div className="relative flex items-center">
                    <button
                      type="button"
                      onClick={() => setIsMenuOpen(!isMenuOpen)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors"
                      aria-label="Importar de integrações"
                    >
                      <i className="fas fa-cloud-arrow-down text-base shrink-0" aria-hidden />
                      Importar de
                      <i className={`fas fa-chevron-down text-xs shrink-0 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} aria-hidden />
                    </button>
                    {isMenuOpen && (
                      <>
                        <div className="fixed inset-0 z-10 bg-black/5" onClick={() => setIsMenuOpen(false)} aria-hidden />
                        <div className="absolute right-0 top-full w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-1 z-[100] min-w-[12rem] mt-2">
                          {isChatwootConfigured && (
                            <button
                              onClick={() => {
                                setIsChatwootSyncModalOpen(true);
                                setIsMenuOpen(false);
                              }}
                              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex items-center gap-2"
                            >
                              <span>💬</span>
                              <span>Chatwoot</span>
                            </button>
                          )}
                          {isPerfexConfigured && (
                            <button
                              onClick={() => {
                                setIsPerfexImportModalOpen(true);
                                setIsMenuOpen(false);
                              }}
                              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex items-center gap-2"
                            >
                              <span>🔧</span>
                              <span>Perfex CRM</span>
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => navigate('/contatos/novo')}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-transparent text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors"
                  aria-label="Criar novo contato"
                >
                  <i className="fas fa-plus text-xl shrink-0" aria-hidden />
                  Novo contato
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 rounded-xl border border-gray-200 sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-y-0 dark:divide-gray-800 dark:border-gray-800">
              <div className="border-b p-5 sm:border-r lg:border-b-0">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-blue-600 dark:text-blue-400 text-xl" aria-hidden>
                    <i className="fas fa-address-book" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Total de contatos</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">
                  {statsLoading ? '–' : (contactStats?.total ?? total)}
                </h3>
              </div>
              <div className="border-b p-5 sm:border-r lg:border-b-0">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-purple-600 dark:text-purple-400 text-xl" aria-hidden>
                    <i className="fas fa-tags" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Categorias</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">
                  {statsLoading ? '–' : (contactStats?.categoriesCount ?? categories.length)}
                </h3>
              </div>
              <div className="border-b p-5 sm:border-r lg:border-b-0">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-teal-600 dark:text-teal-400 text-xl" aria-hidden>
                    <i className="fas fa-sliders" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Campos personalizados</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">
                  {statsLoading ? '–' : (contactStats?.customFieldsCount ?? 0)}
                </h3>
              </div>
              <div className="p-5">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-amber-600 dark:text-amber-400 text-xl" aria-hidden>
                    <i className="fas fa-user-clock" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Sem categoria</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">
                  {statsLoading ? '–' : (contactStats?.withoutCategory ?? 0)}
                </h3>
              </div>
            </div>
          </div>

          {/* Tabela de contatos - mesmo padrão Últimas campanhas / Conexões */}
          <div className="card-padrao overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="font-semibold text-gray-800 dark:text-white/90">Contatos</h2>
                {!hasSelection && contacts.length > 0 && (
                  <button
                    type="button"
                    onClick={handleSelectAllContacts}
                    className="px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    {selectedContactIds.length === contacts.length && selectAllMode === 'page' ? `Desmarcar todos (${contacts.length})` : `Selecionar todos (${contacts.length})`}
                  </button>
                )}
              </div>
              {hasSelection ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    type="button"
                    onClick={handleCancelGlobalSelection}
                    className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 rounded-lg transition-colors"
                  >
                    Desmarcar todas
                  </button>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                    {selectedCount} selecionado{selectedCount > 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={handleOpenBulkEdit}
                    className="btn-header-warning"
                    aria-label="Editar contatos selecionados"
                  >
                    Editar Selecionados
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3 flex-wrap">
                  {(search || selectedCategory) && (
                    <button
                      type="button"
                      onClick={handleClearFilters}
                      className="text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      Limpar
                    </button>
                  )}
                  <div className="flex items-center gap-2">
                    <div className="w-48 sm:w-64 relative">
                      <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden />
                      <input
                        type="text"
                        placeholder="Buscar..."
                        value={search}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div ref={categoryPickerRef}>
                      <button
                        ref={categoryPickerButtonRef}
                        type="button"
                        onClick={() => setCategoryPickerOpen((o) => !o)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[180px] justify-between"
                        aria-expanded={categoryPickerOpen}
                        aria-haspopup="true"
                      >
                        <span className="truncate">
                          {selectedCategory ? categories.find(c => c.id === selectedCategory)?.nome || 'Todas as categorias' : 'Todas as categorias'}
                        </span>
                        <i className={`fas fa-chevron-down text-xs transition-transform ${categoryPickerOpen ? 'rotate-180' : ''}`} aria-hidden />
                      </button>
                      {categoryPickerOpen && categoryPickerPosition && (
                        <Portal>
                          <div
                            className="fixed w-56 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl z-[9999] max-h-80 overflow-y-auto"
                            style={{ top: categoryPickerPosition.top, left: categoryPickerPosition.left }}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                handleCategoryChange('');
                                setCategoryPickerOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 text-sm ${selectedCategory === '' ? 'text-blue-600 dark:text-blue-400 font-medium flex items-center gap-2' : 'text-gray-800 dark:text-gray-200'}`}
                            >
                              {selectedCategory === '' && <i className="fas fa-check text-xs" aria-hidden />}
                              Todas as categorias
                            </button>
                            {categories.map((cat) => (
                              <button
                                key={cat.id}
                                type="button"
                                onClick={() => {
                                  handleCategoryChange(cat.id);
                                  setCategoryPickerOpen(false);
                                }}
                                className={`w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 text-sm ${selectedCategory === cat.id ? 'text-blue-600 dark:text-blue-400 font-medium flex items-center gap-2' : 'text-gray-800 dark:text-gray-200'}`}
                              >
                                {selectedCategory === cat.id && <i className="fas fa-check text-xs" aria-hidden />}
                                {cat.nome}
                              </button>
                            ))}
                          </div>
                        </Portal>
                      )}
                    </div>
                    <div ref={columnPickerRef}>
                      <button
                        ref={columnPickerButtonRef}
                        type="button"
                        onClick={() => setColumnPickerOpen((o) => !o)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        aria-expanded={columnPickerOpen}
                        aria-haspopup="true"
                      >
                        <i className="fas fa-columns text-gray-500 dark:text-gray-400" aria-hidden />
                        Colunas
                        <i className={`fas fa-chevron-down text-xs transition-transform ${columnPickerOpen ? 'rotate-180' : ''}`} aria-hidden />
                      </button>
                      {columnPickerOpen && columnPickerPosition && (
                        <Portal>
                          <div
                            className="fixed w-56 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl z-[9999] max-h-80 overflow-y-auto"
                            style={{ top: columnPickerPosition.top, left: columnPickerPosition.left }}
                          >
                            <p className="px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Exibir colunas
                            </p>
                            {allAvailableColumns.map((col) => (
                              <label
                                key={col.id}
                                className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer text-sm text-gray-800 dark:text-gray-200"
                                onClick={() => toggleColumn(col.id)}
                              >
                                <input
                                  type="checkbox"
                                  checked={visibleColumnIds.includes(col.id)}
                                  onChange={() => {}}
                                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 pointer-events-none"
                                />
                                {col.label}
                              </label>
                            ))}
                            <p className="px-3 pt-1 text-xs text-gray-400 dark:text-gray-500 border-t border-gray-100 dark:border-gray-700 mt-1">
                              Pelo menos uma coluna. Ações sempre visível.
                            </p>
                          </div>
                        </Portal>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16" role="status" aria-label="Carregando contatos">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
                <span className="ml-2 text-gray-600 dark:text-gray-400">Carregando...</span>
              </div>
            ) : contacts.length === 0 ? (
              <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                {total === 0 ? 'Nenhum contato encontrado.' : 'Nenhum contato encontrado com os filtros aplicados.'}
                <p className="mt-2 text-sm">Use <strong>Novo contato</strong> ou <strong>Importar contatos</strong> para começar.</p>
              </div>
            ) : (
              <>
                {selectAllMode === 'page' && selectedContactIds.length === contacts.length && total > contacts.length && (
                  <div className="px-6 pt-4 pb-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <i className="fas fa-info-circle text-blue-600 dark:text-blue-400 shrink-0" aria-hidden />
                        <span className="text-sm text-blue-900 dark:text-blue-200">
                          Todos os <strong>{contacts.length} contatos</strong> desta página estão selecionados.
                        </span>
                        <button type="button" onClick={handleSelectAllGlobal} className="text-sm text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 font-semibold underline shrink-0">
                          Selecionar todos os {total} contatos{search || selectedCategory ? ' (com filtros)' : ''}?
                        </button>
                      </div>
                      <button type="button" onClick={handleCancelGlobalSelection} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 ml-2" aria-label="Fechar">
                        <i className="fas fa-times" aria-hidden />
                      </button>
                    </div>
                  </div>
                )}
                {selectAllMode === 'all' && (
                  <div className="px-6 pt-4 pb-4">
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-check-circle text-green-600 dark:text-green-400" aria-hidden />
                        <span className="text-sm text-green-900 dark:text-green-200">
                          Todos os <strong>{total} contatos</strong>{search || selectedCategory ? ' (com filtros)' : ''} estão selecionados.
                        </span>
                      </div>
                      <button type="button" onClick={handleCancelGlobalSelection} className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 ml-2" aria-label="Cancelar seleção global">
                        <i className="fas fa-times" aria-hidden />
                      </button>
                    </div>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <ContactList
                    contacts={contacts}
                    loading={false}
                    onEdit={handleEdit}
                    onDelete={deleteContact}
                    selectedContactIds={selectedContactIds}
                    onToggleSelect={handleToggleSelectContact}
                    onSelectAll={handleSelectAllContacts}
                    selectionMode={true}
                    onSort={handleSort}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    visibleColumnIds={visibleColumnIds}
                    onVisibleColumnsChange={handleVisibleColumnsChange}
                    allCustomFields={allCustomFields}
                    customFieldColumns={customFieldColumns}
                  />
                </div>
              </>
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}

          <div className="text-center text-sm text-gray-500 dark:text-gray-400">
            Mostrando {contacts.length} de {total} contatos
          </div>
        </div>
      </PageLayout>

      <BulkEditModal
        isOpen={isBulkEditModalOpen}
        onClose={handleCloseBulkEdit}
        selectedContactIds={selectedContactIds}
        onSuccess={handleBulkEditSuccess}
        selectAllMode={selectAllMode}
        search={search}
        selectedCategory={selectedCategory}
      />

      <ChatwootSyncModal
        isOpen={isChatwootSyncModalOpen}
        onClose={() => setIsChatwootSyncModalOpen(false)}
        onSuccess={() => {
          refresh();
          fetchStats();
          setIsChatwootSyncModalOpen(false);
        }}
      />

      <PerfexImportModal
        isOpen={isPerfexImportModalOpen}
        onClose={() => setIsPerfexImportModalOpen(false)}
        onSuccess={() => {
          refresh();
          fetchStats();
          setIsPerfexImportModalOpen(false);
        }}
      />
    </>
  );
}